import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getSolutionReasons, submitSolutionReason } from '../../../firebase/solutionReasonsApi.js';

// Normalize timestamp to millis for sorting
const toMillis = (ts) => {
  try {
    if (!ts) return 0;
    if (typeof ts?.seconds === 'number') return ts.seconds * 1000 + (ts.nanoseconds || 0) / 1e6;
    if (ts instanceof Date) return ts.getTime();
    if (typeof ts === 'string') return new Date(ts).getTime() || 0;
    if (typeof ts === 'number') return ts;
  } catch {}
  return 0;
};

// Async thunks
export const fetchSolutionReasons = createAsyncThunk(
  'solutionView/reasons/fetch',
  async ({ solutionId }) => {
    // Fetch all reasons for the solution (no more tab filtering)
    return await getSolutionReasons(solutionId, 'all', { pageSize: 50, fetchPolicy: 'cache-first' });
  }
);

export const addSolutionReason = createAsyncThunk(
  'solutionView/reasons/add',
  async ({ solutionId, userId, userName, userProfession, reasonType, text, title, parentReasonId = null }) => {
    // Preserve selected reasonType from UI when provided; fall back to 'general' (e.g., for counters)
    const reasonTypeToUse = reasonType || 'general';
    return await submitSolutionReason({ 
      solutionId, 
      userId, 
      userName, 
      userProfession, 
      reasonType: reasonTypeToUse,
      text,
      title,
      parentReasonId // For counter-reasons
    });
  }
);

const reasonsSlice = createSlice({
  name: 'solutionViewReasons',
  initialState: {
    reasonsBySolutionId: {} // { [solutionId]: { reasons: [reasons], loading: false, error: null } }
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSolutionReasons.pending, (state, action) => {
        const { solutionId } = action.meta.arg;
        state.reasonsBySolutionId[solutionId] = state.reasonsBySolutionId[solutionId] || { reasons: [] };
        state.reasonsBySolutionId[solutionId].loading = true;
        state.reasonsBySolutionId[solutionId].error = null;
      })
      .addCase(fetchSolutionReasons.fulfilled, (state, action) => {
        const { solutionId, reasons } = action.payload;
        state.reasonsBySolutionId[solutionId] = state.reasonsBySolutionId[solutionId] || { reasons: [] };
        // Sort by timestamp, newest first, but keep thread structure
        const sorted = buildReasonThreads(reasons || []);
        state.reasonsBySolutionId[solutionId].reasons = sorted;
        state.reasonsBySolutionId[solutionId].loading = false;
        state.reasonsBySolutionId[solutionId].error = null;
      })
      .addCase(fetchSolutionReasons.rejected, (state, action) => {
        const { solutionId } = action.meta.arg;
        state.reasonsBySolutionId[solutionId] = state.reasonsBySolutionId[solutionId] || { reasons: [] };
        state.reasonsBySolutionId[solutionId].loading = false;
        state.reasonsBySolutionId[solutionId].error = action.error.message;
      })
      .addCase(addSolutionReason.fulfilled, (state, action) => {
        const { solutionId, reason } = action.payload;
        state.reasonsBySolutionId[solutionId] = state.reasonsBySolutionId[solutionId] || { reasons: [] };
        
        if (reason.parentReasonId) {
          // This is a counter-reason, find parent and add to its replies
          const parentReason = findReasonById(state.reasonsBySolutionId[solutionId].reasons, reason.parentReasonId);
          if (parentReason) {
            parentReason.counterReasons = parentReason.counterReasons || [];
            parentReason.counterReasons.unshift(reason);
          }
        } else {
          // This is a top-level reason
          state.reasonsBySolutionId[solutionId].reasons.unshift(reason);
        }
      });
  }
});

// Helper function to build threaded reason structure
function buildReasonThreads(reasons) {
  const topLevel = [];
  const reasonsMap = new Map();
  
  // First pass: create map and identify top-level reasons
  reasons.forEach(reason => {
    reason.counterReasons = [];
    reasonsMap.set(reason.id, reason);
    if (!reason.parentReasonId) {
      topLevel.push(reason);
    }
  });
  
  // Second pass: attach counter-reasons to their parents
  reasons.forEach(reason => {
    if (reason.parentReasonId) {
      const parent = reasonsMap.get(reason.parentReasonId);
      if (parent) {
        parent.counterReasons.push(reason);
      }
    }
  });
  
  // Sort top-level by timestamp (newest first)
  topLevel.sort((a, b) => toMillis(b.timestamp) - toMillis(a.timestamp));
  
  // Sort counter-reasons by timestamp (oldest first for conversation flow)
  topLevel.forEach(reason => {
    if (reason.counterReasons.length > 0) {
      reason.counterReasons.sort((a, b) => toMillis(a.timestamp) - toMillis(b.timestamp));
    }
  });
  
  return topLevel;
}

// Helper function to find a reason by ID in nested structure
function findReasonById(reasons, id) {
  for (const reason of reasons) {
    if (reason.id === id) return reason;
    if (reason.counterReasons) {
      const found = findReasonById(reason.counterReasons, id);
      if (found) return found;
    }
  }
  return null;
}

export default reasonsSlice.reducer;
